// client/src/components/unidades/sections/UnidadeImagensSection.js
import React, { useState, useCallback } from 'react';
import { useMutateUnidades } from '../../../hooks/useUnidades';
import { 
    UploadCloud, 
    Loader2, 
    Image as ImageIcon,
    Trash2,
    Star,
    CheckCircle,
    Info,
    AlertCircle
} from 'lucide-react';
import useAlert from '../../../hooks/useAlert';

const UnidadeImagensSection = ({ unidade, onUpdateSuccess, isCreating = false }) => {
    const { setAlert } = useAlert();
    const [uploading, setUploading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Garantir a extração do ID independente do formato
    const targetUnidadeId = unidade?._id || unidade?.id;

    const { 
        addUnidadeImagem, 
        definirImagemPrincipalUnidade,
        definirLogoUnidade,
        removeUnidadeImagem 
    } = useMutateUnidades();

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tamanho (5MB)
            if (file.size > 5 * 1024 * 1024) {
                setAlert('Arquivo muito grande. Tamanho máximo: 5MB', 'warning');
                return;
            }
            
            // Validar tipo
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                setAlert('Tipo de arquivo não suportado. Use JPG, PNG ou WebP.', 'warning');
                return;
            }
            
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleImageUpload = () => {
        if (!targetUnidadeId) {
            setAlert('Unidade não identificada. Recarregue a página e tente novamente.', 'danger');
            return;
        }

        if (!imageFile) return setAlert('Nenhum arquivo selecionado.', 'warning');

        setUploading(true);

        addUnidadeImagem.mutate({ unidadeId: targetUnidadeId, file: imageFile }, {
            onSuccess: (response) => {
                setImageFile(null);
                setImagePreview(null);
                const input = document.getElementById('unidade-image-upload-component');
                if (input) input.value = '';
                
                if (onUpdateSuccess) {
                    onUpdateSuccess();
                }
            },
            onError: (error) => {
                let errorMessage = 'Erro ao enviar imagem';
                if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message.includes('Unexpected field') || error.message.includes('Too many files')) {
                    errorMessage = 'Erro de configuração no servidor. Campo de upload não reconhecido.';
                } else if (error.message.includes('Network Error')) {
                    errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
                }
                
                setAlert(errorMessage, 'danger');
            },
            onSettled: () => {
                setUploading(false);
            }
        });
    };

    const handleSetImagemPrincipal = useCallback((imagemId) => {
        if (!targetUnidadeId || !imagemId) return;

        if (unidade?.imagemPrincipal?.refId?.toString() === imagemId.toString()) {
            setAlert('Esta imagem já é a imagem principal.', 'info');
            return;
        }
        
        definirImagemPrincipalUnidade.mutate({
            unidadeId: targetUnidadeId,
            arquivoId: imagemId
        }, {
            onSuccess: () => {
                if (onUpdateSuccess) onUpdateSuccess();
            }
        });
    }, [targetUnidadeId, unidade?.imagemPrincipal?.refId, definirImagemPrincipalUnidade, setAlert, onUpdateSuccess]);

    const handleSetLogo = useCallback((imagemId) => {
        if (!targetUnidadeId || !imagemId) return;

        const logoAtual = typeof unidade?.logo === 'object' ? (unidade?.logo?._id || unidade?.logo?.id) : unidade?.logo;

        if (logoAtual?.toString() === imagemId.toString()) {
            setAlert('Esta imagem já é o logo.', 'info');
            return;
        }

        definirLogoUnidade.mutate({
            unidadeId: targetUnidadeId,
            arquivoId: imagemId
        }, {
            onSuccess: () => {
                if (onUpdateSuccess) onUpdateSuccess();
            }
        });
    }, [targetUnidadeId, unidade?.logo, definirLogoUnidade, setAlert, onUpdateSuccess]);

    const handleRemoveImagem = useCallback((imagemId) => {
        if (!targetUnidadeId || !imagemId) return;

        if (window.confirm('Tem certeza que deseja remover esta imagem customizada?')) {
            removeUnidadeImagem.mutate({
                unidadeId: targetUnidadeId,
                imagemId: imagemId
            }, {
                onSuccess: () => {
                    if (onUpdateSuccess) onUpdateSuccess();
                }
            });
        }
    }, [targetUnidadeId, removeUnidadeImagem, onUpdateSuccess]);

    const isPrincipal = (img) => {
        const principalId = unidade?.imagemPrincipal?.refId;
        const imgId = img?._id || img?.id;
        if (!principalId || !imgId) return false;
        return principalId.toString() === imgId.toString();
    };

    const isLogo = (img) => {
        const logoId = typeof unidade?.logo === 'object' ? (unidade?.logo?._id || unidade?.logo?.id) : unidade?.logo;
        const imgId = img?._id || img?.id;
        if (!logoId || !imgId) return false;
        return logoId.toString() === imgId.toString();
    };

    if (isCreating) {
        return (
            <div className="form-content">
                <div className="form-header">
                    <div className="flex-1">
                        <h3 className="form-title flex items-center mb-0">
                            <ImageIcon size={20} className="inline mr-2 text-primary" />
                            Imagens da Unidade
                        </h3>
                    </div>
                </div>
                <div className="form-tabs-content">
                    <div className="panel-state-wrapper panel-info">
                        <Info size={24} className="icon"/>
                        <span>
                            Primeiro, salve a unidade na aba 'Dados da Unidade'. Após salvar, as opções de gerenciamento de imagens aparecerão aqui.
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    if (!unidade) {
        return (
            <div className="form-content">
                <div className="form-header">
                    <div className="flex-1">
                        <h3 className="form-title flex items-center mb-0">
                            <ImageIcon size={20} className="inline mr-2 text-primary" />
                            Imagens da Unidade
                        </h3>
                    </div>
                </div>
                <div className="form-tabs-content">
                    <div className="panel-state-wrapper panel-danger">
                        <Info size={24} className="icon"/>
                        <span>Unidade não carregada. Volte e tente novamente.</span>
                    </div>
                </div>
            </div>
        );
    }

    const imagens = unidade.imagens || [];

    return (
        <div className="form-content" disabled={uploading}>
            <div className="form-header flex items-center justify-between mb-4">
                <div className="flex-1">
                    <h3 className="form-title flex items-center mb-0">
                        <ImageIcon size={20} className="inline mr-2 text-primary" />
                        Imagens da Unidade: {unidade.nome}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Gerencie as imagens de capa e logotipo que os clientes visualizarão.
                    </p>
                </div>
            </div>

            <div className="form-tabs-content p-0">
                {/* Adicionar Nova Imagem Customizada */}
                <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm mb-6">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Adicionar Nova Imagem Customizada</h4>
                    <p className="text-sm text-gray-500 mb-4">Faça upload de uma nova imagem específica para esta unidade</p>
                    
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 flex-shrink-0">
                            {imagePreview ? 
                                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md" /> : 
                                <ImageIcon size={40} className="text-gray-300" />
                            }
                        </div>
                        <div className="flex-1 w-full space-y-3">
                            <div>
                                <label htmlFor="unidade-image-upload-component" className="block text-sm font-medium text-gray-700 mb-1">
                                    Escolher arquivo
                                </label>
                                <input 
                                    type="file" 
                                    id="unidade-image-upload-component" 
                                    onChange={handleFileChange} 
                                    accept="image/jpeg, image/jpg, image/png, image/webp" 
                                    className="form-input text-sm w-full" 
                                    disabled={uploading}
                                />
                                <div className="text-xs text-gray-500 mt-1">Formatos: JPG, PNG, WebP • Máx: 5MB</div>
                            </div>
                            
                            {imageFile && (
                                <div className="text-sm text-green-600 flex items-center bg-green-50 p-2 rounded border border-green-100">
                                    <CheckCircle size={14} className="mr-1.5" />
                                    Arquivo selecionado: {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
                                </div>
                            )}

                            <button 
                                type="button" 
                                onClick={handleImageUpload} 
                                disabled={!imageFile || uploading} 
                                className="action-button action-button-primary w-full sm:w-auto mt-2"
                            >
                                {uploading ?
                                    <><Loader2 size={16} className="icon animate-spin"/> Enviando...</> :
                                    <><UploadCloud size={16} className="icon" /> Enviar Imagem</>
                                }
                            </button>
                        </div>
                    </div>
                </div>

                {/* Galeria de Imagens da Unidade */}
                <div className="mt-8">
                    <div className="flex items-center mb-4">
                        <h4 className="text-base font-semibold text-gray-800">
                            Galeria de Imagens da Unidade 
                        </h4>
                        <span className="ml-3 text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200">
                            {imagens.length} {imagens.length === 1 ? 'imagem' : 'imagens'}
                        </span>
                        {imagens.filter(img => isPrincipal(img)).length > 0 && (
                            <span className="ml-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full flex items-center">
                                <Star size={12} className="mr-1" fill="currentColor"/> Principal
                            </span>
                        )}
                    </div>
                    
                    {imagens.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {imagens.map((img, index) => {
                                const imgId = img?._id || img?.id;
                                
                                return (
                                    <div key={imgId || `img-fallback-${index}`} className="relative group border border-gray-200 rounded-lg overflow-hidden shadow-sm aspect-square bg-white hover:shadow-md transition-shadow">
                                        <img 
                                            src={img.url || img.arquivo || '/images/placeholder.png'} 
                                            alt={img.metadata?.originalName || 'Imagem da unidade'} 
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = '/images/default-unidade.png';
                                            }}
                                        />
                                        
                                        {/* Badges */}
                                        <div className="absolute top-1.5 right-1.5 flex flex-col gap-1 items-end z-10">
                                            {isPrincipal(img) && (
                                                <div className="bg-amber-400 text-white p-1.5 rounded-full shadow-sm" title="Imagem Principal">
                                                    <Star size={14} fill="white" />
                                                </div>
                                            )}
                                            {isLogo(img) && (
                                                <div className="bg-blue-500 text-white p-1.5 rounded-full shadow-sm" title="Logo da Unidade">
                                                    <ImageIcon size={14} fill="white" />
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Overlay com ações */}
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                            <button 
                                                type="button"
                                                onClick={() => handleSetImagemPrincipal(imgId)}
                                                disabled={isPrincipal(img) || uploading}
                                                title={isPrincipal(img) ? "Já é a imagem principal" : "Definir como principal"}
                                                className="p-2.5 bg-white rounded-full text-gray-700 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                                            >
                                                <Star size={18} fill={isPrincipal(img) ? "#d97706" : "none"} className={isPrincipal(img) ? "text-amber-600" : ""} />
                                            </button>

                                            <button 
                                                type="button"
                                                onClick={() => handleSetLogo(imgId)}
                                                disabled={isLogo(img) || uploading}
                                                title={isLogo(img) ? "Já é o logo" : "Definir como logo"}
                                                className="p-2.5 bg-white rounded-full text-gray-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                                            >
                                                <ImageIcon size={18} className={isLogo(img) ? "text-blue-600" : ""} />
                                            </button>
                                            
                                            <button 
                                                type="button"
                                                onClick={() => handleRemoveImagem(imgId)}
                                                disabled={uploading}
                                                title="Remover Imagem"
                                                className="p-2.5 bg-red-50 text-red-600 rounded-full hover:bg-red-100 hover:text-red-700 transition-colors shadow-sm"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>

                                        {/* Nome da imagem no hover */}
                                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                            {img.metadata?.originalName || 'Imagem customizada'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="panel-state-wrapper panel-info mt-4 border border-dashed border-blue-200">
                            <ImageIcon size={32} className="icon text-blue-300"/>
                            <span className="text-gray-500">Esta unidade ainda não possui imagens cadastradas. Faça o upload acima.</span>
                        </div>
                    )}
                </div>

                {/* Informações de ajuda */}
                <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                        <Info size={16} className="mr-2 text-primary" />
                        Dicas para gerenciamento de imagens
                    </h5>
                    <ul className="text-sm text-gray-600 space-y-2 ml-1">
                        <li className="flex items-start"><span className="text-gray-400 mr-2">•</span> <strong>Imagem Principal:</strong> Será exibida como capa da unidade nos catálogos e na busca.</li>
                        <li className="flex items-start"><span className="text-gray-400 mr-2">•</span> <strong>Logo:</strong> Será usado nos recibos, contratos e e-mails gerados por esta unidade.</li>
                        <li className="flex items-start"><span className="text-gray-400 mr-2">•</span> <strong>Atualização:</strong> Ao excluir uma foto, ela é apagada permanentemente do sistema.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default UnidadeImagensSection;